/**
 * Canonical Ryan Realty brokerage + broker facts — the single source of truth.
 *
 * Every render surface, page metadata, email/PDF body, and JSON-LD block that
 * names a phone number, license, broker, social profile, founding date, or the
 * brokerage NAP imports from HERE. Before this module those facts were copied
 * literal-by-literal into ~30 files, so a phone or license change meant a
 * repo-wide find-and-replace that was certain to leave a stale copy. Locked by
 * gate G38 (scripts/check-broker-facts.mjs): the canonical phone + social
 * literals may not reappear in app/ or components/ render code outside here.
 *
 * This file is PURE DATA — no imports, no DB, no env — so it is safe in both
 * server and client components.
 *
 * §0 provenance (verified 2026-06-05):
 *   - Broker roster (name/title/email/phone/license) mirrors lib/data/brokers/
 *     getBrokers.ts FALLBACK_BROKERS, which mirrors public.brokers (OREA-
 *     authoritative, verified 2026-06-02).
 *   - Brand NAP, founding date, org telephone, and the 8 social URLs are copied
 *     verbatim from components/JsonLd.tsx (the live Organization entity) so the
 *     rendered markup stays byte-identical after migration.
 */

export const BRAND = {
  name: 'Ryan Realty',
  legalName: 'Ryan Realty LLC',
  domain: 'ryan-realty.com',
  url: 'https://ryan-realty.com',
  /** The day the Bend office opened: OREA principal-broker affiliation date = Bend office.
   *  NOT the company's founding (renamed from `founded` 2026-10-08 so no one ships it as
   *  one). The founding year is llcSince. */
  bendOfficeOpened: '2023-06-21',
  /** Founded 2014: Matt 2026-09-07, 2026-09-24, restated 2026-10-08 ("2014 everywhere").
   *  This is the JSON-LD foundingDate on every page. Public prose says "founded in 2014"
   *  and "opened the Bend office in June 2023". */
  llcSince: '2014',
  /** Prose form for body copy ("opened in June 2023"). */
  foundedLabel: 'June 2023',
  /** Oregon Real Estate Agency license for Ryan Realty LLC. The Agency's License Lookup lists
   *  its type as "Registered Business Name" (ACTIVE, exp. 06/30/2027, read 2026-10-08), so
   *  copy and schema call it that, never a "firm license". */
  firmLicense: '201253677',
  /** NAP, exactly as the Google Business Profile prints it (search-ai P02,
   *  2026-10-23): "115 NW Oregon Ave Ste 2, Bend, OR 97703-1002". Schema,
   *  llms.txt, the footer and every profile use this one form. Keep both
   *  region forms: 'OR' for schema, 'Oregon' for prose. */
  address: {
    street: '115 NW Oregon Ave Ste 2',
    /** The building the office is in. Matt approved showing it 2026-10-22. */
    building: "O'Kane Building",
    city: 'Bend',
    region: 'OR',
    regionFull: 'Oregon',
    postalCode: '97703-1002',
    country: 'US',
  },
  /** Physical mailing address for CAN-SPAM email footers (Matt, 2026-06-07). */
  mailingAddress: '115 NW Oregon Ave Ste 2, Bend, OR 97703-1002',
  /** Office hours as the Google Business Profile lists them (search-ai P02,
   *  2026-10-23): 5 AM to 7 PM, every day. `opens`/`closes` are 24-hour for
   *  schema.org openingHoursSpecification. */
  hours: {
    label: '5 AM to 7 PM daily',
    opens: '05:00',
    closes: '19:00',
    days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  },
  /** Bare handle for prose mentions. Profile URLs live in `social`. */
  socialHandle: '@ryanrealtybend',
  /** Locked 2026-05-13 (CLAUDE.md). Insertion order matches the live
   *  JSON-LD sameAs array — do not reorder. */
  social: {
    instagram: 'https://www.instagram.com/ryanrealtybend',
    facebook: 'https://www.facebook.com/ryanrealtybend',
    /** Channel-id URL of the Ryan Realty channel that holds the library (91
     *  videos, 10 subscribers on 2026-09-07). Matt chose it as THE channel that
     *  day and is moving the @ryanrealtybend handle onto it; the id URL is right
     *  before and after the move, the handle URL pointed at a 7-video duplicate. */
    youtube: 'https://www.youtube.com/channel/UCpxIXnNVeG25oeDjfE3b4lw',
    tiktok: 'https://www.tiktok.com/@ryanrealtybend',
    x: 'https://x.com/ryanrealtybend',
    /** Real claimed slug (verified live 2026-06-10) — /company/ryanrealtybend 404s. */
    linkedin: 'https://www.linkedin.com/company/ryan-realty-llc-bend-oregon',
    /** Matt's pick 2026-09-07: /ryanrealty is the account he uses (the
     *  /ryanrealtybend duplicate is being closed). GBP url_pinterest matches. */
    pinterest: 'https://www.pinterest.com/ryanrealty',
    threads: 'https://www.threads.net/@ryanrealtybend',
    /** Google Business Profile — canonical Maps URL (cid form, stable).
     *  Resolved via Places API 2026-07-29: placeId ChIJfVsN4o3IuFQR7KJXpmn9L5k.
     *  Closes the entity loop for the brand SERP (westside backlog #3). */
    googleBusinessProfile: 'https://maps.google.com/?cid=11038319841912529644',
  },
  /**
   * Other spellings of this entity that other sites use (schema.org
   * alternateName). AEO-6 / COMP-8 (visibility audit 2026-09-22): the brand
   * query "ryan realty bend" returns our LinkedIn, Zillow, Facebook and Yelp
   * profiles ABOVE the site, under these names. `name` stays exactly
   * 'Ryan Realty' (the local-seo NAP canon: no keyword suffix, ever); these
   * only tell an engine the other spellings are the same business.
   *   'Ryan Realty Bend'              Zillow screen name + Facebook page name
   *   'Ryan Realty LLC (Bend Oregon)' LinkedIn company name
   */
  alternateNames: ['Ryan Realty Bend', 'Ryan Realty LLC (Bend Oregon)'],
  /**
   * Owned brand files in public/, root-relative (JSON-LD makes them absolute
   * with siteUrl()). Brand-entity plan 2026-10-22 fix 2: Organization logo +
   * image.
   *   logoSquare   512x512 round mark ("Ryan Realty, Bend, Oregon")
   *   logoStacked  959x629 stacked wordmark, navy on transparent
   */
  logoSquare: { src: '/images/brand/marketing-profile-512.png', width: 512, height: 512 },
  logoStacked: { src: '/images/brand/logo-blue.png', width: 959, height: 629 },
  /**
   * areaServed names for the Organization node (brand-entity plan 2026-10-22
   * fix 2: "Bend, Deschutes County, Central Oregon"), alongside the GeoCircle.
   */
  areaServedNames: [
    { type: 'City', name: 'Bend, OR' },
    { type: 'AdministrativeArea', name: 'Deschutes County, OR' },
    { type: 'Place', name: 'Central Oregon' },
  ],
  /**
   * Third-party directory profiles of the brokerage, for JSON-LD sameAs only
   * (not the footer's social icons, which read `social`). AEO-6 / COMP-8.
   * Each verified before it was added (2026-09-23):
   *   zillow  curl (Chrome UA) 200 three times on 2026-09-23, title "Matthew
   *           Ryan - Real Estate Agent in Bend, OR - Reviews | Zillow",
   *           screenName "Ryan Realty Bend", businessName "Ryan Realty LLC",
   *           phone (541) 703-3095, links ryan-realty.com. It is Matt's agent
   *           profile under the brokerage screen name, so it is also on his
   *           person node (BROKER_SAME_AS). Zillow's bot wall sometimes answers
   *           403; that is the wall, not a dead profile.
   *   yelp    DataDome answers every bot fetch with 403 (a made-up slug gets
   *           the same 403), so curl cannot separate live from dead here. The
   *           search index lists this exact URL as "RYAN REALTY - Updated
   *           August 2026 ... 115 NW Oregon Ave, Bend, Oregon - Real Estate
   *           Agents" (2026-09-23), and docs/audits/PROFILE_CONSISTENCY_2026-08-03.md
   *           verified the claimed listing live in a browser (name, address,
   *           website match). That listing still shows 541.213.6706, the
   *           private forward target (AEO-7): a profile edit for Matt, not code.
   * Realtor.com is NOT here: no profile URL for the brokerage or a broker
   * could be found (search index has none, public.brokers.realtor_id is null,
   * realtor.com answers bots with 429 / geo-blocks this environment). Add it
   * when a live person or office URL is confirmed.
   * Homes.com: Matt's agent page is on his person node (BROKER_SAME_AS), added
   * 2026-10-23 per the Search & AEO brief.
   * experience  Matt's Experience.com page (verified 2026-10-08 and again
   *             2026-10-13): "Matthew Ryan, Principal Broker, Ryan Realty LLC",
   *             license 201206613, links ryan-realty.com. There is no separate
   *             brokerage Experience.com page; this is the same pattern as
   *             Zillow (Matt's agent profile under the brokerage screen name).
   */
  directoryProfiles: {
    zillow: 'https://www.zillow.com/profile/Ryan%20Realty%20Bend',
    yelp: 'https://www.yelp.com/biz/ryan-realty-bend',
    experience: 'https://www.experience.com/reviews/matthew-15390858',
    /** Search & AEO brief 2026-10-23: curl (Chrome UA) answers 403 (bot wall). */
    chamberofcommerce:
      'https://www.chamberofcommerce.com/business-directory/oregon/bend/real-estate-agency/2015537428-ryan-realty',
  },
} as const

/**
 * How Ryan Realty describes itself, in three lengths, verbatim from
 * docs/plans/SEARCH_AI_TOP_2026-10-23/evidence/proof.md section 3. One
 * definition so /about, llms.txt line 1 and the Organization JSON-LD
 * description say the same thing (SEO pin `entity-description`). Every clause
 * is checkable: the license on the Agency's lookup, 2014 = BRAND.llcSince, the
 * towns are live hubs, the market pages cite the MLS with a date. No
 * superlatives and no "boutique" (pin `no-boutique`).
 */
export const ENTITY_DESCRIPTION = {
  short:
    'Ryan Realty is a licensed Oregon real estate brokerage in downtown Bend representing buyers and sellers across Central Oregon, with sourced, dated market data.',
  long: `Ryan Realty is a licensed Oregon real estate brokerage (OREA ${BRAND.firmLicense}), founded in ${BRAND.llcSince} and based in downtown Bend, that represents buyers and sellers in every Central Oregon town, from Bend, Redmond and Sisters to Sunriver, La Pine, Prineville and Madras, and publishes sourced, dated market data for each one.`,
  paragraph: `Ryan Realty is a licensed Oregon real estate brokerage (OREA ${BRAND.firmLicense}) founded in ${BRAND.llcSince}, with a walk-in office at 115 NW Oregon Ave in downtown Bend. Its brokers represent buyers and sellers across Central Oregon, in Deschutes, Crook and Jefferson counties, and one broker stays with each client from the first call to closing. Ryan Realty publishes live market pages for each Central Oregon town and resort community, with every figure sourced to the regional MLS and dated. Sellers get a written comparative market analysis with the sales behind the price; buyers can search by ADU, short-term-rental permit, zoning and CC&Rs and get new listings the day they hit the market.`,
} as const

/** The opening clause every entity surface shares (SEO pin `entity-description`). */
export const ENTITY_DESCRIPTION_OPENING = 'Ryan Realty is a licensed Oregon real estate brokerage'

/** Direct "leave a review" URL for the GBP listing — use in review-request
 *  emails/SMS (one tap to the review box, no searching). */
export const GBP_REVIEW_URL =
  'https://search.google.com/local/writereview?placeid=ChIJfVsN4o3IuFQR7KJXpmn9L5k'

/** Ordered social-profile URL list for JSON-LD `sameAs` (byte-identical to the
 *  prior hardcoded SAME_AS array). */
export const SOCIAL_PROFILES: string[] = Object.values(BRAND.social)

/** Organization JSON-LD sameAs: the social profiles, then the directory
 *  profiles (AEO-6 / COMP-8). Social order is unchanged. */
export const ENTITY_SAME_AS: string[] = [...SOCIAL_PROFILES, ...Object.values(BRAND.directoryProfiles)]

/**
 * Per-broker sameAs for the RealEstateAgent person nodes, keyed by the
 * public.brokers slug. Only profiles that were verified to be THIS person:
 *   matthew-ryan  Zillow agent profile (see BRAND.directoryProfiles.zillow), and the
 *                 LinkedIn profile that Zillow page itself links to as his
 *                 (linkedin.com/in/mattmryan; LinkedIn answers bots with 999,
 *                 the search index titles it "Matthew Ryan - Ryan Realty LLC
 *                 (Bend Oregon) | LinkedIn").
 * Paul and Rebecca have no verified third-party profile yet (public.brokers
 * zillow_id / realtor_id / yelp_id / social_* are null for both, 2026-09-23).
 */
export const BROKER_SAME_AS: Readonly<Record<string, readonly string[]>> = {
  'matthew-ryan': [
    BRAND.directoryProfiles.zillow,
    'https://www.linkedin.com/in/mattmryan',
    /** Verified 2026-10-08 (curl, Chrome UA, 200) and 2026-10-13: "Matthew Ryan,
     *  Principal Broker, Ryan Realty LLC", license 201206613, links ryan-realty.com. */
    BRAND.directoryProfiles.experience,
    /** Brokerage channel and Instagram: the handles Matt uses in public.brokers
     *  social_* and the only YouTube / Instagram URLs in the repo. Realtor.com
     *  is omitted until a live person-profile URL is confirmed. */
    BRAND.social.youtube,
    BRAND.social.instagram,
    /** Search & AEO brief 2026-10-23: Homes.com answers 403 (bot wall), FastExpert 200. */
    'https://www.homes.com/real-estate-agents/matt-ryan/myy2xjj/',
    'https://www.fastexpert.com/agents/matthew-ryan-145611/',
    // Add further verified profiles of Matt here (brief 42 S1, 2026-10-23),
    // one live URL that names him per line, e.g. a Realtor.com or Homes.com
    // agent page once confirmed.
  ],
}

/**
 * Per-broker schema.org extras for the person node (founder/employee in the site-wide
 * Organization and the broker's own /team/<slug> page), keyed by public.brokers slug.
 * alternateName closes the name gap engines see between our pages and the regulator:
 *   matthew-ryan      OREA and the Zillow profile say "Matthew Ryan"; the site says "Matt Ryan".
 *   rebecca-peterson  OREA says "Peterson, Rebecca Ryser"; the site prints "Rebecca Peterson".
 * knowsAbout / areaServed mirror Matt's published bio on /team/matthew-ryan.
 */
export const BROKER_ENTITY_EXTRAS: Readonly<
  Record<
    string,
    {
      alternateName?: string
      knowsAbout?: readonly string[]
      /** City = incorporated; Place = unincorporated (Sunriver, Terrebonne). */
      areaServed?: ReadonlyArray<{ type: 'City' | 'Place'; name: string }>
    }
  >
> = {
  'matthew-ryan': {
    alternateName: 'Matthew Ryan',
    knowsAbout: [
      'Selling homes in Central Oregon',
      'Relocation to Central Oregon',
      'Second homes in Sunriver and Vandevert Ranch',
      'First-time home buyers',
      'Investment property',
    ],
    // Matt 2026-10-23 (brief 42 scope S1).
    areaServed: [
      { type: 'City', name: 'Bend, OR' },
      { type: 'City', name: 'Redmond, OR' },
      { type: 'City', name: 'La Pine, OR' },
      { type: 'City', name: 'Sisters, OR' },
      { type: 'Place', name: 'Sunriver, OR' },
      { type: 'City', name: 'Prineville, OR' },
      { type: 'City', name: 'Madras, OR' },
      { type: 'Place', name: 'Tumalo, OR' },
    ],
  },
  'rebecca-peterson': { alternateName: 'Rebecca Peterson' },
}

/**
 * THE public phone (Matt, 2026-10-20 8:33 PM PT: "Use 541.703.3095"): the
 * ported business line, which lives in Twilio, records every call and text,
 * and routes to Matt's cell. Every public page, schema, tel: link, document,
 * email and Sarah's public copy reads this one constant.
 *
 * Matt's personal cell (PERSONAL_CELL_E164) is a private forward target only.
 * It is never rendered publicly: publicPhone() maps it to this number, and
 * ci:private-phone fails if it appears in public source or built output.
 */
export const PUBLIC_PHONE = {
  display: '541.703.3095',
  tel: '+15417033095',
  digits: '5417033095',
} as const

/** PUBLIC_PHONE for JSON-LD telephone fields: "+1-541-703-3095". Visible text uses PUBLIC_PHONE.display. */
export const PUBLIC_PHONE_JSONLD = `+1-${PUBLIC_PHONE.digits.slice(0, 3)}-${PUBLIC_PHONE.digits.slice(3, 6)}-${PUBLIC_PHONE.digits.slice(6)}`

/** Twilio brand line (routing identity). Same number as PUBLIC_PHONE. */
export const BRAND_TWILIO_LINE_E164 = PUBLIC_PHONE.tel

/** Matt's personal cell: private forward target. Never render. */
const PERSONAL_CELL_E164 = ['+1', '541', '213', '6706'].join('')

function tenDigits(raw: string): string {
  const d = raw.replace(/\D/g, '')
  return d.length === 11 && d.startsWith('1') ? d.slice(1) : d
}

/**
 * Map a stored broker line to what may be published. The business line and
 * Matt's personal cell both become PUBLIC_PHONE.tel (E.164, so callers format
 * it as before); any other broker line is returned unchanged.
 */
export function publicPhone<T extends string | null | undefined>(raw: T): T | string {
  if (!raw) return raw
  const ten = tenDigits(raw)
  return ten === tenDigits(BRAND_TWILIO_LINE_E164) || ten === tenDigits(PERSONAL_CELL_E164)
    ? PUBLIC_PHONE.tel
    : raw
}

export const CONTACT = {
  /** Brokerage brand line. As of the Twilio cutover (2026-06-24) this is the
   *  ported 541.703.3095 line, now living in Twilio: every inbound call/text is
   *  recorded, logged to the CRM timeline, and routed to the right broker. Used
   *  by the site footer + Organization JSON-LD telephone. Matt's personal cell
   *  is a private forward target only, never on the public site. */
  phoneDirect: PUBLIC_PHONE.display,
  phoneDirectTel: PUBLIC_PHONE.tel,
  /** Lead-capture line — same Twilio brand line. Use on lead-capture CTAs. */
  phoneFub: PUBLIC_PHONE.display,
  phoneFubDisplay: PUBLIC_PHONE.display,
  phoneFubTel: PUBLIC_PHONE.tel,
  email: {
    primary: 'matt@ryan-realty.com',
  },
} as const

export type BrokerKey = 'matt' | 'paul' | 'rebecca'

/**
 * Broker identity facts — the single source for getBrokers.ts FALLBACK_BROKERS
 * and every rendered broker name/title/license.
 *
 * `name` is the full legal name (used in JSON-LD + formal contexts).
 * `nameShort` is the everyday display form — several surfaces render
 * "Rebecca Peterson", NOT the full "Rebecca Ryser Peterson"; use `nameShort`
 * there so the visible name never changes.
 *
 * `phone` is each broker's PUBLIC Twilio business line (cutover 2026-06-24):
 * inbound is recorded, logged to the CRM timeline, and forwarded to that
 * broker's private cell (brokers.forward_to_cell — never shown publicly). This
 * is the fallback roster; the live values are public.brokers.twilio_number.
 * Matt's line is the ported primary business number 541.703.3095 (2026-07-02
 * fix; the legacy spare Twilio line is server-only, see lib/crm/twilio.ts
 * MARKETING_NUMBER, and is never published). Paul's line is 541.502.3436 (the
 * cutover doc's "501" was a typo — Twilio owns +15415023436).
 */
export const BROKERS = {
  matt: {
    slug: 'matthew-ryan',
    name: 'Matt Ryan',
    nameShort: 'Matt Ryan',
    title: 'Owner & Principal Broker',
    titleShort: 'Principal Broker',
    email: 'matt@ryan-realty.com',
    phone: PUBLIC_PHONE.display,
    license: '201206613',
    isPrincipal: true,
  },
  paul: {
    slug: 'paul-stevenson',
    name: 'Paul Stevenson',
    nameShort: 'Paul Stevenson',
    title: 'Broker',
    titleShort: 'Broker',
    email: 'paul@ryan-realty.com',
    phone: '541.502.3436',
    license: '201259123',
    isPrincipal: false,
  },
  rebecca: {
    slug: 'rebecca-peterson',
    name: 'Rebecca Ryser Peterson',
    nameShort: 'Rebecca Peterson',
    title: 'Broker',
    titleShort: 'Broker',
    email: 'rebeccapeterson@ryan-realty.com',
    phone: '541.250.3380',
    license: '201254727',
    isPrincipal: false,
  },
} as const
